import LegalLayout, { LegalList, Section } from "@/components/LegalLayout";
import { CONTROLLER_ADDRESS, CONTROLLER_NAME, PRIVACY_CONTACT_EMAIL } from "@/lib/legal";

export default function PrivacyPage() {
  return (
    <LegalLayout eyebrow="Legal" title="Privacy Notice" updated="3 September 2026">
      <Section title="1. Who is responsible">
        <p>
          The controller for Callsect account administration and private-beta operations is {CONTROLLER_NAME}, {CONTROLLER_ADDRESS}. Privacy and
          support requests can be sent to <a className="text-indigo-300 hover:text-indigo-200" href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>.
        </p>
        <p>Callsect is currently operated as a limited private beta for professional users.</p>
        <p>
          When a professional user connects a work meeting account or uploads a work call on behalf of an employer or another organisation,
          that organisation may be the controller for the call content and Callsect may act as its service
          provider or processor. The user and their organisation must determine the correct roles, lawful
          instructions and participant notices before importing or uploading conversations. This notice does not itself
          create a data processing agreement with an organisation.
        </p>
      </Section>

      <Section title="2. Data we process">
        <LegalList>
          <li>Account data: name, email address, optional password hash, linked sign-in provider and stable provider identifier, account type and legal-acceptance records.</li>
          <li>Call data: title, customer or company label, call date, transcript, speaker segments, duration and transcription metadata.</li>
          <li>Integration data: connected Google account identity, encrypted OAuth credentials, sync timestamps, Meet conference identifiers and participant display names.</li>
          <li>Generated data: dissection, CRM notes, coaching, next-call preparation, relationship timelines and Writing DNA profiles.</li>
          <li>Operational data: beta quota counters, rate-limit records, first-party onboarding and feature-use events, and limited technical request information.</li>
        </LegalList>
        <p>
          Account, integration and upload data come from the user or the connected meeting provider. Transcripts and generated results are derived
          from an authorised Google Meet transcript, a user-supplied full transcript, or an uploaded recording. Account credentials and the information required to process a call are
          necessary to provide the service. Profile labels, Writing DNA and optional metadata are voluntary.
          Product events record only limited workflow facts such as whether a guide was completed, an upload
          was started, a result section was opened or a CRM note was generated. They do not include transcript
          text, call titles, customer names or raw audio.
        </p>
      </Section>

      <Section title="3. Audio and transcript lifecycle">
        <ol className="list-decimal space-y-3 pl-5 marker:text-indigo-400">
          <li>For manual upload, the backend writes audio to temporary server storage for validation and sends it to AssemblyAI&apos;s EU API endpoint for transcription, language detection, speaker labels and timestamps.</li>
          <li>After retrieving a manual-upload result, Callsect requests immediate deletion of the AssemblyAI transcript and associated audio; the temporary server file is also deleted, including failure paths.</li>
          <li>For Google Meet, Callsect uses the user&apos;s encrypted OAuth connection to read generated transcript entries and participant metadata through Google&apos;s read-only Meet API. It does not download the original Meet recording.</li>
          <li>If a participant cannot be matched safely to one existing Customer, the transcript waits in Meeting Inbox until the user confirms the Customer. Dismissal removes the staged transcript and keeps only a minimal source identifier to prevent re-import.</li>
          <li>For direct transcript import, Callsect parses the uploaded file or pasted text into a short-lived preview. The user must confirm the YOU/CLIENT mapping before analysis. Unconfirmed previews expire after one hour; confirmed preview content is removed after the saved call is created.</li>
          <li>Direct transcript imports are not sent to AssemblyAI. Summary-only notes and one-speaker transcripts are rejected before analysis.</li>
          <li>The resulting transcript, not the original audio, is sent to OpenAI&apos;s API to generate dissection, CRM notes, coaching, next-call preparation and relationship memory.</li>
        </ol>
        <p>
          The original recording is not stored in the Callsect database. If an AssemblyAI upload succeeds but
          no transcript identifier is created, or if a remote deletion request fails, immediate deletion cannot
          be confirmed and the provider&apos;s standard retention rules may apply. Such failures are recorded in
          backend logs for operational follow-up.
        </p>
      </Section>

      <Section title="4. Why we use the data">
        <LegalList>
          <li>To connect authorised meeting accounts and provide transcript ingestion, transcription, dissection, CRM-note, coaching, next-call preparation and relationship-memory features.</li>
          <li>To authenticate users, secure the service and prevent abuse.</li>
          <li>To administer quotas, measure beta activation, identify onboarding friction and operate the private beta.</li>
          <li>To meet legal obligations and establish or defend legal claims where necessary.</li>
        </LegalList>
        <p>
          The principal legal bases are performance of the private-beta agreement (Article 6(1)(b) GDPR),
          legitimate interests in operating and securing the service and preventing abuse (Article 6(1)(f)),
          and compliance with legal obligations where applicable (Article 6(1)(c)). Consent is relied on only
          where the relevant law requires it.
        </p>
        <p>
          Where consent or notice is required for recording, transcription, import or processing, the user and
          their organisation are responsible for satisfying those requirements before connecting or uploading. OAuth authorisation and the in-app
          confirmation record the user&apos;s instructions; they do not prove that every
          legal, employment, confidentiality or contractual requirement has been met.
        </p>
      </Section>

      <Section title="5. Service providers and international processing">
        <LegalList>
          <li>Render: application hosting and temporary server-side audio processing.</li>
          <li>MongoDB Atlas: storage of account data, transcripts, metadata and generated results.</li>
          <li>Zoho Mail: mailbox hosting and receipt of privacy, support and beta-administration correspondence.</li>
          <li>Resend: outbound delivery of transactional emails such as beta invitations, email verification, password resets and owner notifications.</li>
          <li>AssemblyAI: audio transcription, language detection, speaker diarization and timestamps through the configured EU API endpoint.</li>
          <li>Google: account authentication using the user&apos;s stable Google identifier, name and email; and, only when separately connected, read-only access to generated Google Meet transcripts, conference metadata and participant display names.</li>
          <li>OpenAI: analysis and generation from transcript text; the original audio is not sent to OpenAI in the current implementation.</li>
        </LegalList>
        <p>
          Providers may use subprocessors and may process limited account, usage, security or support data
          outside Germany or the EEA under their current contractual transfer safeguards. Use of an EU API
          endpoint does not remove the need to review the provider&apos;s DPA, subprocessors and international-transfer arrangements.
        </p>
        <p>
          Callsect does not intentionally submit call content for provider model training. OpenAI states that
          API inputs and outputs are not used for training by default unless the API customer opts in. Callsect
          sends audio to AssemblyAI through its European endpoint; AssemblyAI states that files submitted through
          its European servers are not used for model training. A change away from that endpoint requires a fresh
          legal and notice review.
        </p>
      </Section>

      <Section title="6. Retention and deletion">
        <LegalList>
          <li>Saved account and call data remain until the user deletes the relevant call or account.</li>
          <li>The Google Sign-In identity is removed when unlinked or when the account is deleted. Google Meet OAuth credentials are encrypted at rest and removed when the user disconnects Meet or deletes the account.</li>
          <li>Unmatched Meet transcripts remain in Meeting Inbox until confirmed, dismissed, disconnected or the account is deleted. Dismissed items retain only a minimal deduplication identifier.</li>
          <li>Unconfirmed direct-transcript previews expire automatically after one hour and are deleted immediately when dismissed or when the account is deleted.</li>
          <li>Callsect requests deletion of the AssemblyAI transcript and associated uploaded audio immediately after retrieving the transcription result.</li>
          <li>The temporary Render-side audio file is deleted after the transcription attempt.</li>
          <li>OpenAI states that default API abuse-monitoring logs may retain customer content for up to 30 days, subject to legal exceptions and any approved account-level retention controls.</li>
          <li>Short-lived security and rate-limit records expire automatically or are deleted with the account.</li>
          <li>User-linked onboarding and feature-use events are included in account export and deleted with the account.</li>
          <li>After account deletion, a minimal beta access-control record containing the email address, deleted status and deletion timestamp is retained for the duration of the beta to prevent accidental recreation and abuse.</li>
          <li>Account-unlinked aggregate beta counters may remain for spend-control integrity.</li>
          <li>Infrastructure logs and backups may follow provider-configured retention periods.</li>
        </LegalList>
        <p>
          Users can delete individual calls, export their account data as JSON and permanently delete their
          account in the app. Deleting a call removes its saved Callsect transcript, analysis, CRM
          notes and coaching and updates or removes the related customer-memory record.
        </p>
      </Section>

      <Section title="7. Your rights">
        <p>
          Subject to applicable law, you may request access, correction, deletion, restriction, portability
          or object to processing. You may withdraw consent where consent is the legal basis, without affecting
          earlier lawful processing. Requests should be sent to <a className="text-indigo-300 hover:text-indigo-200" href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>.
        </p>
        <p>
          You may also complain to a competent data-protection authority. For a private-sector controller
          established in Bavaria, the relevant authority is generally the Bavarian State Office for Data
          Protection Supervision (BayLDA).
        </p>
      </Section>

      <Section title="8. Data protection safeguards">
        <p>
          Callsect applies technical and organisational safeguards designed to protect personal data against
          unauthorised access, disclosure, alteration and loss. These safeguards include:
        </p>
        <LegalList>
          <li>HTTPS encryption while data is transmitted between the user, Callsect and its service providers.</li>
          <li>Encryption at rest for stored Google Meet OAuth credentials. Application credentials and encryption keys are kept in restricted server-side environment configuration and are not exposed to the browser.</li>
          <li>Authenticated, user-scoped access controls so users can access only data associated with their own account. Passwords are stored as salted hashes rather than plaintext.</li>
          <li>A restricted production-origin allowlist, server-side rate limits and short-lived OAuth state records to reduce unauthorised requests and abuse.</li>
          <li>Data minimisation, temporary-file cleanup, limited transcript-preview retention and user-controlled deletion as described in this notice.</li>
          <li>Operational access limited to the operator and service providers where reasonably necessary to run, secure or troubleshoot the service, respond to the user, or comply with law. Suspected security incidents are investigated and affected credentials can be revoked or rotated.</li>
        </LegalList>
        <p>
          No internet service can guarantee absolute security. Questions or reports concerning data security can
          be sent to <a className="text-indigo-300 hover:text-indigo-200" href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>.
        </p>
      </Section>

      <Section title="9. Google API Services User Data and Limited Use">
        <p>
          The use and transfer of raw or derived user data received from Google Workspace APIs by Callsect
          adheres to the{" "}
          <a
            className="text-indigo-300 hover:text-indigo-200"
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noreferrer"
          >
            Google API Services User Data Policy
          </a>, including the Limited Use requirements.
        </p>
        <p>
          Google Workspace data is used only to provide or improve the user-facing Callsect features described
          in this notice. It is not sold, used for advertising, used to determine creditworthiness or lending,
          or transferred for unrelated purposes. Callsect does not permit humans to read Google Workspace data
          except with the user&apos;s affirmative agreement for support, where necessary for security or abuse
          investigation, to comply with applicable law, or where the data has been aggregated and anonymised.
          Transfers to the service providers identified above are limited to what is necessary to provide those
          user-facing features and are subject to applicable confidentiality and data-protection obligations.
        </p>
      </Section>

      <Section title="10. Automated analysis and security">
        <p>
          Callsect generates probabilistic AI-assisted observations and coaching. They may be incomplete or
          wrong and are not used by Callsect to make legal or similarly significant decisions about people.
          Passwords are stored as hashes, access is authenticated, and stored user data is scoped to the
          signed-in account.
        </p>
      </Section>

      <Section title="11. Changes">
        <p>
          Material changes require a new legal version. Existing users must review and accept the current
          Privacy Notice and Private Beta Terms before returning to protected features.
        </p>
        <p>
          The 3 September 2026 update clarifies existing data-protection safeguards and Google API Limited Use
          commitments. It does not expand the categories of data collected or the purposes for which they are used.
        </p>
      </Section>
    </LegalLayout>
  );
}
